import { useCallback, useEffect, useState } from "react";
import { Pencil, X, RefreshCw, Search, Eye } from "lucide-react";
import api, { fmtErr, inr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminCouriers() {
  const [couriers, setCouriers] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [providers, setProviders] = useState([]);
  const [search, setSearch] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pvOpen, setPvOpen] = useState(false);
  const [pvForm, setPvForm] = useState({ pickup: "500011", delivery: "110001", weight: "0.5" });
  const [pvResult, setPvResult] = useState(null);
  const [pvLoading, setPvLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/couriers").then(({ data }) => setCouriers(data)).catch(() => {});
    api.get("/admin/providers").then(({ data }) => setProviders(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/couriers/sync");
      if (data.status === "started") {
        // Backend runs the sync in the background (can exceed the request timeout on slow
        // provider egress) — poll the courier list until counts settle.
        toast.info("Courier sync started — takes up to a minute, the list refreshes automatically", { duration: 8000 });
        let ticks = 0;
        const poll = setInterval(() => {
          load();
          ticks += 1;
          if (ticks >= 5) {
            clearInterval(poll);
            setSyncing(false);
            toast.success("Courier sync should be complete — list refreshed");
          }
        }, 15000);
        return;
      }
      const parts = Object.entries(data.providers || {}).map(([k, v]) => `${k}: ${v.status}${v.couriers != null ? ` (${v.couriers})` : ""}`);
      const errs = Object.entries(data.providers || {}).flatMap(([k, v]) => (v.errors || []).concat(v.error ? [v.error] : []).map((e) => `${k} ${e}`));
      if (errs.length) {
        toast.warning(`Courier sync — ${parts.join(" · ")}`, { description: errs.slice(0, 3).join(" | "), duration: 12000 });
      } else {
        toast.success(`Courier sync — ${parts.join(" · ")}`);
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); setSyncing(false); }
  };

  const toggle = async (c) => {
    // No silent failures: a rejected PUT previously vanished as an unhandled rejection,
    // leaving the panel unchanged with zero feedback (owner's 9pm disables never landed).
    try {
      await api.put(`/admin/couriers/${c.courier_id}`, { enabled: !c.enabled });
      toast.success(`${c.name} ${c.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (e) { toast.error(fmtErr(e, "Toggle failed — not saved")); }
  };

  const q = search.trim().toLowerCase();
  const visible = (couriers || []).filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || (c.provider_code || "").toLowerCase().includes(q));

  const bulkSet = async (enabled) => {
    if (!visible.length || bulkBusy) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/couriers/bulk-status", { courier_ids: visible.map((c) => c.courier_id), enabled });
      toast.success(`${data.modified} courier(s) ${enabled ? "enabled" : "disabled"}${q ? ` matching "${search.trim()}"` : ""}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBulkBusy(false); }
  };

  const runPreview = async () => {
    setPvLoading(true);
    setPvResult(null);
    try {
      // Same endpoint customers hit — this is the exact customer-facing rate list.
      const { data } = await api.post("/rates/calculate", {
        pickup_pincode: pvForm.pickup, delivery_pincode: pvForm.delivery,
        weight: parseFloat(pvForm.weight) || 0.5, payment_mode: "Prepaid",
      });
      setPvResult(data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setPvLoading(false); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ priority: c.priority, margin_pct: c.margin_pct, min_weight: c.min_weight, max_weight: c.max_weight, cod_supported: c.cod_supported, eta_days: c.eta_days, provider_code: c.provider_code });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/couriers/${editing.courier_id}`, {
        priority: Number(form.priority), margin_pct: Number(form.margin_pct),
        min_weight: Number(form.min_weight), max_weight: Number(form.max_weight),
        cod_supported: !!form.cod_supported, eta_days: Number(form.eta_days),
        provider_code: form.provider_code,
      });
      toast.success("Courier updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-couriers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Couriers</h1>
          <p className="text-sm text-slate-500">Real courier catalog synced from live provider APIs — rates, ETAs and COD support fetched directly.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={() => setPvOpen(!pvOpen)} data-testid="courier-preview-toggle-btn"
            className="flex items-center gap-2 rounded-xl border border-shipany-blue/30 px-4 py-2.5 text-sm font-bold text-shipany-blue hover:bg-shipany-blue/5">
            <Eye className="h-4 w-4" /> Customer view
          </button>
          <button onClick={sync} disabled={syncing} data-testid="couriers-sync-btn"
            className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync from Providers"}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="couriers-toolbar">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="courier-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courier, code or provider — e.g. dtdc" className={`${inputCls} pl-10`} />
        </div>
        <button onClick={() => bulkSet(true)} disabled={bulkBusy || !visible.length} data-testid="courier-enable-filtered-btn"
          className="rounded-xl bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-600 hover:bg-emerald-100 disabled:opacity-50">
          Enable {q ? "filtered" : "all"}
        </button>
        <button onClick={() => bulkSet(false)} disabled={bulkBusy || !visible.length} data-testid="courier-disable-filtered-btn"
          className="rounded-xl bg-red-50 px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-100 disabled:opacity-50">
          Disable {q ? "filtered" : "all"}
        </button>
        <span className="mono text-xs text-slate-400" data-testid="couriers-visible-count">{visible.length} of {couriers?.length ?? 0} shown</span>
      </div>

      {pvOpen && (
        <div className="mt-4 rounded-2xl border border-shipany-blue/20 bg-shipany-blue/[0.03] p-5" data-testid="customer-view-preview">
          <p className="text-xs font-bold uppercase tracking-widest text-shipany-blue">Exactly what customers see — live engine, your current courier selection</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Pickup</label>
              <input data-testid="preview-pickup-input" maxLength={6} inputMode="numeric" value={pvForm.pickup} onChange={(e) => setPvForm({ ...pvForm, pickup: e.target.value.replace(/\D/g, "") })} className={`${inputCls} mono mt-1 w-32`} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Delivery</label>
              <input data-testid="preview-delivery-input" maxLength={6} inputMode="numeric" value={pvForm.delivery} onChange={(e) => setPvForm({ ...pvForm, delivery: e.target.value.replace(/\D/g, "") })} className={`${inputCls} mono mt-1 w-32`} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Weight (kg)</label>
              <input data-testid="preview-weight-input" inputMode="decimal" value={pvForm.weight} onChange={(e) => setPvForm({ ...pvForm, weight: e.target.value })} className={`${inputCls} mono mt-1 w-24`} /></div>
            <button onClick={runPreview} disabled={pvLoading} data-testid="preview-run-btn"
              className="rounded-xl bg-shipany-blue px-5 py-2 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
              {pvLoading ? "Loading…" : "Preview rates"}
            </button>
          </div>
          {pvResult && (
            <div className="mt-4" data-testid="preview-results">
              <p className="mono text-xs text-slate-400">{pvForm.pickup} → {pvForm.delivery} · {pvForm.weight} kg · {(pvResult.quotes || []).length} operators visible</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(pvResult.quotes || []).map((qt) => (
                  <div key={qt.courier_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`preview-quote-${qt.courier_code}`}>
                    <p className="font-semibold text-shipany-navy">{qt.courier_name}</p>
                    <p className="mono mt-1 text-lg font-bold text-shipany-blue">{inr(qt.final_price)}</p>
                    <p className="text-xs text-slate-400">{qt.eta_days}d ETA</p>
                    {(qt.badges || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1">{qt.badges.map((b) => <span key={b} className="rounded-full bg-shipany-cyan/15 px-2 py-0.5 text-[10px] font-bold uppercase text-shipany-blue">{b}</span>)}</div>}
                  </div>
                ))}
              </div>
              {(pvResult.quotes || []).length === 0 && <p className="mt-2 text-sm text-slate-400">No operators visible for this lane/weight with your current selection.</p>}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="couriers-table">
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Courier</th><th className="px-4 py-3.5">Provider</th><th className="px-4 py-3.5">Mode</th>
              <th className="px-4 py-3.5">ETA</th><th className="px-4 py-3.5">Weight Range</th><th className="px-4 py-3.5">COD</th>
              <th className="px-4 py-3.5">Margin %</th><th className="px-4 py-3.5">Priority</th><th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.courier_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`courier-row-${c.code}`}>
                <td className="px-5 py-3.5"><p className="font-semibold text-shipany-navy">{c.name}</p><p className="mono text-[10px] text-slate-400">{c.code} · {c.tier}</p></td>
                <td className="px-4 py-3.5 capitalize text-slate-500">{c.provider_code}</td>
                <td className="px-4 py-3.5 capitalize text-slate-500">{c.mode}</td>
                <td className="mono px-4 py-3.5">{c.eta_days}d</td>
                <td className="mono px-4 py-3.5 text-slate-500">{c.min_weight}–{c.max_weight} kg</td>
                <td className="px-4 py-3.5">{c.cod_supported ? <span className="text-emerald-600 font-semibold text-xs">YES</span> : <span className="text-slate-400 text-xs">NO</span>}</td>
                <td className="mono px-4 py-3.5">{c.margin_pct}%</td>
                <td className="mono px-4 py-3.5">P{c.priority}</td>
                <td className="px-4 py-3.5">
                  <button onClick={() => toggle(c)} data-testid={`courier-toggle-${c.code}`}
                    className={`relative h-5 w-9 rounded-full transition-colors ${c.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${c.enabled ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-4 py-3.5">
                  <button onClick={() => openEdit(c)} data-testid={`courier-edit-${c.code}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><Pencil className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="courier-edit-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Edit {editing.name}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3.5">
              {[["priority", "Priority"], ["margin_pct", "Margin %"], ["min_weight", "Min weight (kg)"], ["max_weight", "Max weight (kg)"], ["eta_days", "ETA (days)"]].map(([k, l]) => (
                <div key={k}><label className="text-xs font-semibold text-slate-500">{l}</label>
                  <input data-testid={`courier-form-${k}`} type="number" step="0.1" className={`${inputCls} mono mt-1`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
              ))}
              <div className="col-span-2"><label className="text-xs font-semibold text-slate-500">Route bookings via provider</label>
                <select data-testid="courier-form-provider" className={`${inputCls} mt-1`} value={form.provider_code || ""} onChange={(e) => setForm({ ...form, provider_code: e.target.value })}>
                  {(providers || []).filter((p) => p.enabled).map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                </select>
                <p className="mt-1 text-[10px] text-slate-400">Admin authority: e.g. route Delhivery via one provider, XpressBees via another.</p></div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <input type="checkbox" data-testid="courier-form-cod" checked={!!form.cod_supported} onChange={(e) => setForm({ ...form, cod_supported: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
                COD supported
              </label>
            </div>
            <button data-testid="courier-save-btn" className="mt-6 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Save Courier</button>
          </form>
        </div>
      )}
    </div>
  );
}
